/**
 * Permission hooks — reactive checks for use in components.
 *
 * Named to mirror the Lambda @require_* decorators for consistency:
 *
 *   useIsManager()                  → @require_manager
 *   useIsProgramAdminOrOwner(id)    → @require_program_admin_or_owner
 *   useCanCreateProgram()           → @require_manager (isManager OR isAdmin)
 *
 * BUSINESS RULE: Roles are mutually exclusive per cohort.
 * A user can be admin OR mentor OR participant — never multiple roles
 * in the same cohort. Different roles are allowed across different cohorts.
 */
import { useAuthStore } from '../stores/authStore';

// ── Role enum ─────────────────────────────────────────────────────────────────

/**
 * Enum of all possible cohort-level roles.
 * Use with useRole() to avoid magic strings in components.
 */
export enum CohortRole {
  PROGRAM_ADMIN = 'admin',
  MENTOR        = 'mentor',
  PARTICIPANT   = 'participant',
  VISITOR       = 'visitor',
}

// ── Global role checks ────────────────────────────────────────────────────────

/** Returns true if the user has the cohort-manager-admin POSIX group flag. */
export function useIsAppAdmin(): boolean {
  return useAuthStore((state) => state.user?.isAdmin ?? false);
}

/** Returns true if the user has the isManager flag (people manager). */
export function useIsManager(): boolean {
  return useAuthStore((state) => state.user?.isManager ?? false);
}

/**
 * Returns true if the user can create or edit programs.
 * Requires manager flag OR cohort-manager-admin POSIX group membership.
 */
export function useCanCreateProgram(): boolean {
  return useAuthStore((state) =>
    (state.user?.isManager ?? false) || (state.user?.isAdmin ?? false)
  );
}

// ── Program-scoped role checks ────────────────────────────────────────────────

/** Returns true if the user is a program admin for the given programId. */
export function useIsProgramAdminOrOwner(programId: string | undefined): boolean {
  return useAuthStore((state) =>
    programId ? state.isAdminOrOwnerFor(programId) : false
  );
}

// ── Cohort-scoped role checks (internal — used by useRole) ───────────────────

function useIsMentor(cohortId: string | undefined): boolean {
  return useAuthStore((state) =>
    cohortId ? state.isMentorFor(cohortId) : false
  );
}

function useIsParticipant(cohortId: string | undefined): boolean {
  return useAuthStore((state) =>
    cohortId ? state.isParticipantIn(cohortId) : false
  );
}

// ── Derived role ──────────────────────────────────────────────────────────────

/**
 * Returns the user's single role for a cohort as a CohortRole enum value.
 * Priority order: PROGRAM_ADMIN > MENTOR > PARTICIPANT > VISITOR.
 *
 * Usage:
 *   const role = useRole(cohort.programId, cohort.cohortId);
 *   if (role === CohortRole.PROGRAM_ADMIN) return <AdminView />;
 *   if (role === CohortRole.MENTOR)        return <MentorView />;
 *   if (role === CohortRole.PARTICIPANT)   return <ParticipantView />;
 *   return <VisitorView />;
 */
export function useRole(
  programId: string | undefined,
  cohortId: string | undefined,
): CohortRole {
  const isAdmin       = useIsProgramAdminOrOwner(programId);
  const isMentor      = useIsMentor(cohortId);
  const isParticipant = useIsParticipant(cohortId);

  if (isAdmin)       return CohortRole.PROGRAM_ADMIN;
  if (isMentor)      return CohortRole.MENTOR;
  if (isParticipant) return CohortRole.PARTICIPANT;
  return CohortRole.VISITOR;
}
